const POSTCODES_IO_BASE = 'https://api.postcodes.io';

export const normalizePostcode = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

const UK_POSTCODE_PATTERN = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/;

export const isCompleteUkPostcode = (value) => UK_POSTCODE_PATTERN.test(normalizePostcode(value));

export const fetchPostcodeSuggestions = async (partial, { signal, limit = 10 } = {}) => {
  const q = normalizePostcode(partial).replace(/\s/g, '');
  if (q.length < 2) return [];
  const url = `${POSTCODES_IO_BASE}/postcodes/${encodeURIComponent(q)}/autocomplete?limit=${limit}`;
  const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!res.ok) return [];
  const json = await res.json().catch(() => null);
  return Array.isArray(json?.result) ? json.result.map(normalizePostcode) : [];
};

const pickString = (obj, keys) => {
  for (const key of keys) {
    const v = obj?.[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

const pickNumberString = (obj, keys) => {
  for (const key of keys) {
    const v = obj?.[key];
    if (v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v))) return String(v);
  }
  return '';
};

const splitTownFromAddress = (full) => {
  const parts = String(full || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return { line1: String(full || '').trim(), town: '' };
  const town = parts[parts.length - 1];
  if (UK_POSTCODE_PATTERN.test(normalizePostcode(town))) return { line1: parts.join(', '), town: '' };
  return { line1: parts.slice(0, -1).join(', '), town };
};

const extractAddressArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const wrappers = [
    'postcodes',
    'Postcodes',
    'addresses',
    'Addresses',
    'addressList',
    'AddressList',
    'data',
    'Data',
    'results',
    'Results',
    'items',
    'Items',
    'result',
    'Result',
  ];
  for (const key of wrappers) {
    if (Array.isArray(payload[key])) return payload[key];
    if (payload[key] && typeof payload[key] === 'object') {
      const nested = extractAddressArray(payload[key]);
      if (nested.length) return nested;
    }
  }
  return [];
};

export const parseDeliveryAddressList = (payload, fallbackPostcode = '') => {
  const rows = extractAddressArray(payload);
  const seen = new Set();
  const out = [];

  rows.forEach((row, index) => {
    let item;
    if (typeof row === 'string') {
      const label = row.trim();
      if (!label) return;
      item = {
        label,
        address1: label,
        address2: '',
        city: '',
        postCode: normalizePostcode(fallbackPostcode),
        state: '',
        latitude: '',
        longitude: '',
      };
    } else if (row && typeof row === 'object') {
      const line1 = pickString(row, ['addressLine1', 'AddressLine1', 'address1', 'Address1', 'line1', 'Line1', 'address_1']);
      const line2 = pickString(row, ['addressLine2', 'AddressLine2', 'address2', 'Address2', 'line2', 'Line2', 'address_2']);
      const city = pickString(row, ['town', 'Town', 'city', 'City', 'postTown', 'PostTown', 'countyTown', 'CountyTown']);
      const postCode = normalizePostcode(
        pickString(row, ['postCode', 'PostCode', 'postcode', 'Postcode', 'zip', 'Zip']) || fallbackPostcode
      );
      const full = pickString(row, [
        'completeAddress',
        'CompleteAddress',
        'fullAddress',
        'FullAddress',
        'formattedAddress',
        'FormattedAddress',
        'address',
        'Address',
        'name',
        'Name',
        'text',
        'Text',
      ]);
      const label = full || [line1, line2, city].filter(Boolean).join(', ');
      if (!label) return;
      const split = !line1 && !city && full ? splitTownFromAddress(full) : null;
      item = {
        label,
        address1: line1 || split?.line1 || full,
        address2: line2,
        city: city || split?.town || '',
        state: pickString(row, ['county', 'County', 'state', 'State']),
        postCode,
        latitude: pickNumberString(row, ['latitude', 'Latitude', 'lat', 'Lat']),
        longitude: pickNumberString(row, ['longitude', 'Longitude', 'long', 'Long', 'lng', 'Lng', 'lon', 'Lon']),
      };
    } else {
      return;
    }

    const id = `${item.label}|${item.postCode}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ ...item, id: `${index}:${id}` });
  });

  return out;
};

export const formatDeliveryAddressLine = (address) => {
  if (!address) return '';
  const label = String(address.label || '').trim();
  const city = String(address.city || '').trim();

  if (city && !label.toLowerCase().includes(city.toLowerCase())) return `${label}, ${city}`;
  return label;
};

const toMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};


export const parseDeliveryAreaResponse = (payload, fallbackPostcode = '') => {
  const addresses = parseDeliveryAddressList(payload, fallbackPostcode);
  const body = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  const flag = body.isAvailable ?? body.IsAvailable;
  const isAvailable = typeof flag === 'boolean' ? flag : addresses.length > 0;
  return {
    isAvailable,
    message: String(body.message ?? body.Message ?? '').trim(),
    minimumOrder: toMoney(body.minimumOrder ?? body.MinimumOrder),
    deliveryRateId: Number.isFinite(Number(body.deliveryRateId)) ? Number(body.deliveryRateId) : 0,
    deliveryRate: toMoney(body.deliveryRate ?? body.DeliveryRate),
    applyDiscountAfter: toMoney(body.applyDiscountAfter ?? body.ApplyDiscountAfter),
    discountedCharges: toMoney(body.discountedCharges ?? body.DiscountedCharges),
    addresses,
  };
};


export const computeDeliveryCharge = (area, subtotal) => {
  if (!area || !area.isAvailable) return 0;
  const sub = toMoney(subtotal);
  if (area.applyDiscountAfter > 0 && sub >= area.applyDiscountAfter) {
    return Math.round(area.discountedCharges * 100) / 100;
  }
  return Math.round(area.deliveryRate * 100) / 100;
};